import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Menu, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface SidebarContextValue {
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
}

const SidebarContext = React.createContext<SidebarContextValue | undefined>(undefined);

interface SidebarProviderProps {
  children: React.ReactNode;
  defaultCollapsed?: boolean;
}

export function SidebarProvider({ children, defaultCollapsed = false }: SidebarProviderProps) {
  const [collapsed, setCollapsed] = React.useState(defaultCollapsed);

  return <SidebarContext.Provider value={{ collapsed, setCollapsed }}>{children}</SidebarContext.Provider>;
}

export function useSidebar() {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within SidebarProvider");
  }
  return context;
}

interface SidebarProps {
  children?: React.ReactNode;
  className?: string;
}

export function Sidebar({ children, className }: SidebarProps) {
  const { collapsed, setCollapsed } = useSidebar();
  const [isLargeScreen, setIsLargeScreen] = React.useState(window.innerWidth >= 1024);
  const [isSheetOpen, setIsSheetOpen] = React.useState(false);

  React.useEffect(() => {
    const handleResize = () => {
      const largeScreen = window.innerWidth >= 1024;
      setIsLargeScreen(largeScreen);
      setCollapsed(collapsed && largeScreen);
      document.getElementById("main-content")?.style.setProperty("--chat-top-margin", largeScreen ? "0" : "1.5rem");
      document.getElementById("main-content")?.style.setProperty("--chat-left-margin", largeScreen ? "0" : "-0.5rem");
    };
    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, [collapsed, setCollapsed]);

  if (!isLargeScreen) {
    return (
      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon" className="lg:hidden absolute left-0 top-0 z-50">
            <Menu className="h-6 w-6" />
            <span className="sr-only">Toggle Sidebar</span>
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="p-0 !w-[271px]">
          <div className="flex flex-col h-full">{children}</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <div
      className={cn(
        "h-full border-r bg-background transition-all duration-300 relative",
        collapsed ? "w-[6rem]" : "w-[271px]",
        className,
      )}
    >
      {children}
    </div>
  );
}

interface SidebarHeaderProps {
  children?: React.ReactNode;
  className?: string;
}

export function SidebarHeader({ children, className }: SidebarHeaderProps) {
  return <div className={cn("flex h-16 items-center px-4", className)}>{children}</div>;
}

interface SidebarContentProps {
  children?: React.ReactNode;
  className?: string;
}

export function SidebarContent({ children, className }: SidebarContentProps) {
  return <div className={cn("flex-1", className)}>{children}</div>;
}

interface SidebarFooterProps {
  children?: React.ReactNode;
  className?: string;
}

export function SidebarFooter({ children, className }: SidebarFooterProps) {
  return <div className={cn("mt-auto", className)}>{children}</div>;
}

interface SidebarGroupProps {
  children?: React.ReactNode;
  className?: string;
}

export function SidebarGroup({ children, className }: SidebarGroupProps) {
  return <div className={cn("px-2 py-2", className)}>{children}</div>;
}

interface SidebarGroupContentProps {
  children?: React.ReactNode;
  className?: string;
}

export function SidebarGroupContent({ children, className }: SidebarGroupContentProps) {
  return <div className={cn("w-full", className)}>{children}</div>;
}

interface SidebarMenuProps {
  children?: React.ReactNode;
  className?: string;
}

export function SidebarMenu({ children, className }: SidebarMenuProps) {
  return <ul className={cn("flex w-full min-w-0 flex-col gap-1", className)}>{children}</ul>;
}

interface SidebarMenuItemProps {
  children?: React.ReactNode;
  className?: string;
}

export function SidebarMenuItem({ children, className }: SidebarMenuItemProps) {
  return <li className={cn("list-none", className)}>{children}</li>;
}

interface SidebarMenuButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
  isActive?: boolean;
}

export const SidebarMenuButton = React.forwardRef<HTMLButtonElement, SidebarMenuButtonProps>(
  ({ className, asChild = false, isActive = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";

    return (
      <Comp
        ref={ref}
        data-active={isActive}
        className={cn(
          "flex w-full items-center gap-2 overflow-hidden rounded-md px-2 py-2 text-left text-sm transition-colors hover:bg-accent hover:text-accent-foreground",
          isActive && "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground",
          className,
        )}
        {...props}
      />
    );
  },
);
SidebarMenuButton.displayName = "SidebarMenuButton";

interface SidebarMenuSubProps extends React.HTMLAttributes<HTMLUListElement> {
  children?: React.ReactNode;
}

export function SidebarMenuSub({ children, className, ...props }: SidebarMenuSubProps) {
  return (
    <ul className={cn("mt-1 ml-4 flex min-w-0 flex-col gap-1 border-l pl-2", className)} {...props}>
      {children}
    </ul>
  );
}

interface SidebarMenuSubItemProps {
  children?: React.ReactNode;
  className?: string;
}

export function SidebarMenuSubItem({ children, className }: SidebarMenuSubItemProps) {
  return <li className={cn("list-none", className)}>{children}</li>;
}

export function SidebarTrigger() {
  const { collapsed, setCollapsed } = useSidebar();
  const isLargeScreen = window.innerWidth >= 1024;

  if (!isLargeScreen) return null;

  return (
    <Button variant="ghost" size="icon" className="ml-auto" onClick={() => setCollapsed(!collapsed)}>
      {collapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
      <span className="sr-only">{collapsed ? "Expand Sidebar" : "Collapse Sidebar"}</span>
    </Button>
  );
}
